import {
  type ChangeEvent as ReactChangeEvent,
  type FocusEvent as ReactFocusEvent,
  forwardRef,
  type KeyboardEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { applyStep } from './utils/calculations.js';
import { checkFormat, parseNumber } from './utils/format.js';
import { addError, getErrorKey } from './utils/validation.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { FocusEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import type { BaseInputProps } from '../_base-input/base-input.types.js';
import { BaseInput } from '../_base-input/BaseInput.js';
import { BaseInputButton } from '../_base-input/BaseInputButton.js';
import {
  BaseInputPrefix,
  BaseInputSuffix,
} from '../_base-input/BaseInputContent.js';
import type { ValidateFieldOptions } from '../form/form.types.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import {
  getErrorType,
  getI18nValidationMessage,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { getSafeRegionalFormat } from '../forms-core/time/helpers.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';
import {
  NativeValidityStateKeys,
  type ValidationState,
  type ValidationTrigger,
} from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import { getValidationState } from '../validation/validation-builders.js';

const COMPONENT_NAME = 'NumberInput';
// Matches a fully valid number: optional sign, integer digits, optional decimal part, optional scientific notation (e.g. "-1.5e+3")
const VALID_PATTERN = '^[+\\-]?\\d+(?:[\\.,]\\d+)?(?:[eE][+\\-]?\\d+)?$';
const VALID_REGEXP = new RegExp(VALID_PATTERN);
// Matches a number that may use any locale's grouping/decimal separators (spaces, dots, commas, Arabic/Unicode variants)
// — used to detect paste-formatted input that can be normalized
const FORMATTABLE_PATTERN =
  /^[-+]?[\d.,'\u2019 ٫٬\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]+(?:[eE][-+]?\d+)?$/u;
// Matches a number that is syntactically incomplete but still being typed:
// digits and separators are optional so intermediate states like "-", "1.", "1.5e" are allowed without rejecting the input
const INTERMEDIATE_VALID_PATTERN = /^[+-]?\d*(?:[.,]\d*)?(?:[eE][+-]?\d*)?$/;

// #region Typings
/**
 * Number inputs let users enter and edit integers and floating-point numbers.
 * @public
 */
export type NumberInputProps = Pick<
  BaseInputProps,
  | 'placeholder'
  | 'variant'
  | 'onKeyUp'
  | 'onKeyDown'
  | 'className'
  | 'style'
  | 'data-testid'
  | 'data-dtrum-mask'
  | 'data-dtrum-allow'
> &
  FormControlProps<number | null, (value: number | null) => void> &
  PropsWithChildren &
  // DEPRECATION: APPDEV-18319 - Remove deprecated `controlId`.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  FocusEvents &
  BehaviorTrackingProps & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
    /** Minimum amount of the input.
     * @defaultValue undefined */
    min?: number;
    /** Maximum amount of the input.
     * @defaultValue undefined */
    max?: number;
    /** Defines the step in which increments/decrements can be performed on the input and validates the value.
     * Must be a positive number greater than 0. Values that are negative, zero, NaN, or undefined default to 1.
     * @defaultValue undefined */
    step?: number;
    /** Multiplier applied to `step` (or 1 if no `step` is set) to determine how much the value changes
     * when using arrow keys or the scroll wheel. Must be a positive integer greater than 0.
     * Values that are negative, zero, NaN, or undefined default to 1. Decimal values are truncated to the nearest integer.
     * @defaultValue 1 */
    stepMultiplier?: number;
  };

// #endregion

/**
 * Number inputs let users enter and edit integers and floating-point numbers.
 * @public
 */
const NumberInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  NumberInputProps
>((props, forwardedRef) => {
  const intl = useIntl();

  const defaultPlaceholder = intl.formatMessage({
    defaultMessage: 'Add number',
    id: 'rrkUQoVNb46Jbd/8',
    description: 'Default placeholder for the number input.',
  });

  const {
    onChange,
    onKeyDown,
    onBlur,
    id: propId,
    name,
    _form,
    _messageTarget,
    value: propValue,
    defaultValue,
    required,
    readOnly = false,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    min,
    max,
    step,
    stepMultiplier = 1,
    placeholder = defaultPlaceholder,
    onValidityChange,
    ...remainingProps
  }: NumberInputProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const { dispatch } = useFormFieldMessagesContext();
  const formValidityChangeRef = useRef<
    ((state: ValidationState) => void) | undefined
  >(undefined);
  const emitBaseValidationState = useValidationStateEmitter((state) =>
    formValidityChangeRef.current?.(state),
  );

  const emitValidationState = useCustomMessageEmitter(
    emitBaseValidationState,
    () => {
      if (!value && value !== 0) {
        const nativeErrorKey = getErrorType(
          internalRef.current?.inputRef?.validity,
        );
        const message = nativeErrorKey
          ? getI18nValidationMessage(
              intl,
              nativeErrorKey,
              internalRef.current?.inputRef,
            )
          : undefined;
        return getValidationState(
          internalRef.current?.inputRef,
          'manual',
          message,
        );
      }
      const errorKey = getErrorKey(value, min, max, step);
      return errorKey
        ? getValidationState(
            NativeValidityStateKeys[errorKey],
            'manual',
            getI18nValidationMessage(
              intl,
              errorKey,
              internalRef.current?.inputRef,
            ),
          )
        : getValidationState(null, 'manual');
    },
  );
  const errorId = useId();
  const isPasted = useRef<boolean>(false);
  const isInternalChanged = useRef<boolean>(defaultValue !== undefined);

  const internalRef = useRef<FormControlRef<HTMLDivElement>>(null);
  const inputRef = useMergeRefs([forwardedRef, internalRef]);

  const [internalValue, setInternalValue] = useState<string | number | null>(
    defaultValue ?? propValue ?? null,
  );

  // Uses the native prototype setter so React's internal value tracker stays in sync.
  const setValueNative = useCallback(
    (newValue: number | string | null, triggerChange: boolean = true) => {
      const input = internalRef.current?.inputRef;
      if (input) {
        let stringValue: string;
        if (newValue === null) {
          stringValue = '';
        } else if (typeof newValue === 'number') {
          stringValue = new Intl.NumberFormat(getSafeRegionalFormat(), {
            useGrouping: false,
            maximumFractionDigits: 17,
          }).format(newValue);
        } else {
          stringValue = String(newValue);
        }
        const desc = Object.getOwnPropertyDescriptor(
          HTMLInputElement.prototype,
          'value',
        );
        desc?.set?.call(input, stringValue);
        input.value = stringValue;
        if (triggerChange) {
          const parsedValue = checkFormat(stringValue);
          isInternalChanged.current = true;
          setInternalValue(parsedValue || stringValue);
          if (VALID_REGEXP.exec(parsedValue)) {
            onChange?.(parseNumber(parsedValue));
          } else if (parsedValue === '') {
            onChange?.(null);
          }
        }
      }
    },
    [onChange],
  );

  // Caching the value to keep not valid states in the input
  const value = useMemo<string | number | null>(() => {
    const isChanged = isInternalChanged.current;
    isInternalChanged.current = false;

    if (isChanged) {
      return internalValue;
    }

    if (!propValue && propValue !== 0) {
      return '';
    }

    const formattedPropValue = new Intl.NumberFormat(getSafeRegionalFormat(), {
      useGrouping: false,
      maximumFractionDigits: 17, // Max precision for a JS number
    }).format(propValue);

    return formattedPropValue;
  }, [propValue, internalValue]);

  const generatedId = useId();
  const id = propId ?? generatedId;

  const getNumberValidationState = useCallback(
    (trigger: ValidationTrigger): ValidationState => {
      if (!value && value !== 0) {
        const nativeErrorKey = getErrorType(
          internalRef.current?.inputRef?.validity,
        );
        const message = nativeErrorKey
          ? getI18nValidationMessage(
              intl,
              nativeErrorKey,
              internalRef.current?.inputRef,
            )
          : undefined;
        return getValidationState(
          internalRef.current?.inputRef,
          trigger,
          message,
        );
      }

      const errorKey = getErrorKey(value, min, max, step);
      return errorKey
        ? getValidationState(
            NativeValidityStateKeys[errorKey],
            trigger,
            getI18nValidationMessage(
              intl,
              errorKey,
              internalRef.current?.inputRef,
            ),
            id,
          )
        : getValidationState(null, trigger);
    },
    [id, intl, max, min, step, value],
  );

  const validateField = useCallback(
    ({ reveal, trigger }: ValidateFieldOptions): ValidationState => {
      internalRef.current?.inputRef?.checkValidity();
      if (reveal) {
        dispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
      return emitValidationState(getNumberValidationState(trigger));
    },
    [dispatch, emitValidationState, getNumberValidationState],
  );

  const formControl = useInternalFormRegistration({
    id,
    name,
    form: _form,
    messageTarget: _messageTarget,
    onValidityChange,
    getValue: () =>
      value === '' || value === null ? null : parseNumber(value),
    focus: () => internalRef.current?.focus?.(),
    validateField: (options) => Promise.resolve(validateField(options)),
    resetValidation: () => {
      internalRef.current?.inputRef?.setCustomValidity('');
      dispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: false, resetShowError: true },
      });
      dispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: { id: errorId, messageType: 'formcontrol', controlId: id },
      });
    },
  });
  formValidityChangeRef.current = formControl.onValidityChange;

  const handleChange = useCallback(
    (e: ReactChangeEvent<HTMLInputElement>) => {
      const parsedValue = checkFormat(e.target.value);

      if (VALID_REGEXP.exec(parsedValue)) {
        onChange?.(parseNumber(parsedValue));
      }

      if (parsedValue === '') {
        onChange?.(null);
      }

      if (isPasted.current && FORMATTABLE_PATTERN.exec(parsedValue)) {
        isInternalChanged.current = true;
        setInternalValue(parsedValue);
        setValueNative(parsedValue, false);
      } else if (INTERMEDIATE_VALID_PATTERN.exec(e.target.value)) {
        isInternalChanged.current = true;
        setInternalValue(parsedValue);
      } else {
        setValueNative(value ?? '', false);
      }
      isPasted.current = false;
    },
    [onChange, setInternalValue, value, setValueNative],
  );

  const handleBlur = useCallback(
    (e: ReactFocusEvent<HTMLInputElement>) => {
      if (!value && value !== 0) {
        const nativeErrorKey = getErrorType(
          internalRef.current?.inputRef?.validity,
        );
        const message = nativeErrorKey
          ? getI18nValidationMessage(
              intl,
              nativeErrorKey,
              internalRef.current?.inputRef,
            )
          : undefined;
        emitValidationState(
          getValidationState(internalRef.current?.inputRef, 'blur', message),
        );
        onBlur?.(e);
        return;
      }

      const errorKey = getErrorKey(value, min, max, step);
      if (errorKey) {
        emitValidationState(
          getValidationState(
            NativeValidityStateKeys[errorKey],
            'blur',
            getI18nValidationMessage(
              intl,
              errorKey,
              internalRef.current?.inputRef,
            ),
          ),
        );
      } else {
        emitValidationState(getValidationState(null, 'blur'));
      }
      onBlur?.(e);
    },
    [value, min, max, step, emitValidationState, onBlur, intl],
  );

  // Handling step changes with buttons, keyboard and wheel
  const handleStep = useCallback(
    (direction: 1 | -1) => {
      setValueNative(
        applyStep(value, step, stepMultiplier, min, max, direction),
      );
    },
    [value, stepMultiplier, step, min, max, setValueNative],
  );

  // Handling keyboard input for step changes and home/end keys
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (readOnly) {
        onKeyDown?.(e);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        handleStep(+1);
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleStep(-1);
      }

      if (e.key === 'Home' && min !== undefined) {
        e.preventDefault();
        setValueNative(min);
      }

      if (e.key === 'End' && max !== undefined) {
        e.preventDefault();
        setValueNative(max);
      }
      onKeyDown?.(e);
    },
    [handleStep, onKeyDown, setValueNative, min, max, readOnly],
  );

  // Handling wheel input
  useEffect(() => {
    const el = internalRef?.current;

    if (!el) {
      return;
    }

    const onWheel = (e: WheelEvent) => {
      if (document.activeElement !== e.target) {
        return;
      }
      e.preventDefault();
      handleStep(e.deltaY < 0 ? +1 : -1);
    };

    // Passive event listeners do not work, because the default action needs to be prevented
    // so we need to explicitly set passive to false
    el.element?.addEventListener('wheel', onWheel, { passive: false });
    return () => el.element?.removeEventListener('wheel', onWheel);
  }, [handleStep]);

  useImperativeHandle(
    forwardedRef,
    () => {
      const base = internalRef.current ?? {
        element: null,
        validate: () => undefined,
      };
      return {
        ...base,
        validateAsync: async () => {
          dispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          const validationState = getNumberValidationState('manual');
          emitValidationState(validationState);
          return validationState;
        },
      };
    },
    [dispatch, getNumberValidationState, emitValidationState],
  );

  // Validating the native number input props manually, as we need to use a type="text" input and can't rely on the native checks.
  // We can't use type="number" because it doesn't support all the formatting and localization features we need.
  useEffect(() => {
    if (!value && value !== 0) {
      dispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: { id: errorId, messageType: 'formcontrol' },
      });
      const nativeErrorKey = getErrorType(
        internalRef.current?.inputRef?.validity,
      );
      if (nativeErrorKey) {
        addError(
          nativeErrorKey,
          errorId,
          internalRef.current?.inputRef,
          dispatch,
        );
      }
      const message = nativeErrorKey
        ? getI18nValidationMessage(
            intl,
            nativeErrorKey,
            internalRef.current?.inputRef,
          )
        : undefined;
      emitValidationState(
        getValidationState(internalRef.current?.inputRef, 'change', message),
      );
      return;
    }

    const errorKey = getErrorKey(value, min, max, step);
    if (errorKey) {
      addError(
        errorKey,
        errorId,
        internalRef.current?.inputRef,
        dispatch,
        true,
      );
      emitValidationState(
        getValidationState(
          NativeValidityStateKeys[errorKey],
          'change',
          getI18nValidationMessage(
            intl,
            errorKey,
            internalRef.current?.inputRef,
          ),
        ),
      );
      return;
    }

    dispatch?.({
      type: FormFieldMessagesAction.REMOVEMESSAGE,
      payload: { id: errorId, messageType: 'formcontrol' },
    });
    internalRef.current?.inputRef?.setCustomValidity('');
    emitValidationState(getValidationState(null, 'change'));
  }, [value, min, max, step, dispatch, errorId, emitValidationState, intl]);

  return (
    <BaseInput
      ref={inputRef}
      {...mergeProps(restProps, behavioralTrackingProps)}
      id={id}
      name={name}
      form={_form}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={value ?? ''}
      required={required}
      readOnly={readOnly}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onPaste={() => (isPasted.current = true)}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-disabled={ariaDisabledProp}
      data-dt-component={COMPONENT_NAME}
      min={min}
      max={max}
      step={step}
      pattern={VALID_PATTERN}
      placeholder={placeholder}
    />
  );
});

/**
 * Use `NumberInputV2` to let users enter integers and floating-point numbers.
 * @public
 */
export const NumberInput = Object.assign(NumberInputRoot, {
  Prefix: BaseInputPrefix,
  Suffix: BaseInputSuffix,
  Button: BaseInputButton,
});

(NumberInput as typeof NumberInput & { displayName: string }).displayName =
  COMPONENT_NAME;
